import React, { useState, useEffect, useCallback } from 'react';
import { suppliesApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Package, Plus, Trash2, X, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';

function SupplyItem({ item, onToggle, onDelete }) {
  return (
    <div className="flex items-center gap-2 py-1.5 group" data-testid={`supply-${item.id}`}>
      <button onClick={() => onToggle(item.id, !item.checked)}
        className={`w-5 h-5 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition-colors
          ${item.checked ? 'bg-[#4A9D6E] border-[#4A9D6E]' : 'border-[#33383F] hover:border-[#4A9D6E]'}`}
        data-testid={`supply-check-${item.id}`}>
        {item.checked && <Check className="w-3 h-3 text-white" />}
      </button>
      <div className="flex-1 min-w-0">
        <p className={`text-sm truncate ${item.checked ? 'text-[#555D68] line-through' : 'text-[#F0EDE8]'}`}>
          {item.name}
        </p>
        {(item.quantity || item.notes) && (
          <p className="text-xs text-[#555D68] truncate">
            {item.quantity && <span>{item.quantity}</span>}
            {item.quantity && item.notes && <span> &middot; </span>}
            {item.notes && <span>{item.notes}</span>}
          </p>
        )}
      </div>
      <Button size="icon" variant="ghost"
        className="h-6 w-6 opacity-0 group-hover:opacity-100 text-[#E55B5B] hover:text-[#E55B5B] hover:bg-[#E55B5B]/10 rounded-lg"
        onClick={() => onDelete(item.id)} data-testid={`delete-supply-${item.id}`}>
        <Trash2 className="w-3 h-3" />
      </Button>
    </div>
  );
}

export function SuppliesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState('');
  const [quantity, setQuantity] = useState('');

  const fetchSupplies = useCallback(async () => {
    try {
      const res = await suppliesApi.getAll();
      setItems(res.data);
    } catch (err) { void err; }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSupplies(); }, [fetchSupplies]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      const res = await suppliesApi.create({ name: name.trim(), quantity: quantity.trim() });
      setItems(prev => [res.data, ...prev]);
      setName('');
      setQuantity('');
      setShowAdd(false);
      toast.success('Supply added');
    } catch (err) { toast.error('Failed to add'); void err; }
  };

  const handleToggle = useCallback(async (id, checked) => {
    try {
      await suppliesApi.update(id, { checked });
      setItems(prev => prev.map(i => i.id === id ? { ...i, checked } : i));
    } catch (err) { toast.error('Failed to update'); void err; }
  }, []);

  const handleDelete = useCallback(async (id) => {
    try {
      await suppliesApi.delete(id);
      setItems(prev => prev.filter(i => i.id !== id));
      toast.success('Removed');
    } catch (err) { toast.error('Failed to remove'); void err; }
  }, []);

  const uncheckedCount = items.filter(i => !i.checked).length;

  if (loading) {
    return (
      <div className="bg-[#23272E] border border-[#33383F] rounded-2xl p-4 h-full flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-[#4A9D6E]" />
      </div>
    );
  }

  return (
    <div className="bg-[#23272E] border border-[#33383F] rounded-2xl p-4 h-full" data-testid="supplies-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Package className="w-4 h-4 text-[#E5A84B]" />
          <h3 className="font-bold text-[#F0EDE8] tracking-tight">Supplies</h3>
        </div>
        <Badge variant="secondary" className="bg-[#2C3038] text-[#9CA3AF] rounded-xl">
          {uncheckedCount} needed
        </Badge>
      </div>

      <ScrollArea className="h-[250px]">
        <div className="space-y-0.5">
          {items.length === 0 ? (
            <div className="text-center py-6 text-[#555D68]">
              <Package className="w-8 h-8 mx-auto mb-2 text-[#33383F]" />
              <p className="text-sm">No supplies listed</p>
            </div>
          ) : (
            <>
              {/* Unchecked first */}
              {items.filter(i => !i.checked).map(item => (
                <SupplyItem key={item.id} item={item} onToggle={handleToggle} onDelete={handleDelete} />
              ))}
              {/* Checked items */}
              {items.filter(i => i.checked).length > 0 && (
                <>
                  <div className="pt-2 pb-1">
                    <p className="accent-text text-[#555D68]">COMPLETED</p>
                  </div>
                  {items.filter(i => i.checked).map(item => (
                    <SupplyItem key={item.id} item={item} onToggle={handleToggle} onDelete={handleDelete} />
                  ))}
                </>
              )}
            </>
          )}
        </div>
      </ScrollArea>

      {/* Add form */}
      <div className="mt-3">
        {showAdd ? (
          <form onSubmit={handleAdd} className="space-y-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus
              placeholder="Supply name..."
              className="h-8 text-sm bg-[#1A1D21] border-[#33383F] text-[#F0EDE8] placeholder:text-[#555D68] focus:border-[#4A9D6E] focus:ring-1 focus:ring-[#4A9D6E] rounded-xl"
              data-testid="supply-name-input" />
            <Input value={quantity} onChange={(e) => setQuantity(e.target.value)}
              placeholder="Qty (optional)"
              className="h-8 text-sm bg-[#1A1D21] border-[#33383F] text-[#F0EDE8] placeholder:text-[#555D68] focus:border-[#4A9D6E] focus:ring-1 focus:ring-[#4A9D6E] rounded-xl"
              data-testid="supply-qty-input" />
            <div className="flex gap-2">
              <Button type="submit" size="sm" className="flex-1 h-8 bg-[#4A9D6E] hover:bg-[#3D8A5E] text-white rounded-xl"
                data-testid="save-supply-button">Add</Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => setShowAdd(false)}
                className="h-8 text-[#9CA3AF] hover:bg-[#2C3038] rounded-xl"><X className="w-3 h-3" /></Button>
            </div>
          </form>
        ) : (
          <Button variant="outline" size="sm" onClick={() => setShowAdd(true)}
            className="w-full h-8 border-[#33383F] text-[#9CA3AF] hover:bg-[#2C3038] rounded-xl text-xs"
            data-testid="add-supply-button">
            <Plus className="w-3 h-3 mr-1" />Add Supply
          </Button>
        )}
      </div>
    </div>
  );
}
